import React, { useState, useMemo, useEffect } from 'react';
import { RegulifyService } from '../api/RegulifyService';
import ContentTitle from '../layout/ContentTitle';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const AdverseEvents = () => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // State for filters
    const [eventTypeFilter, setEventTypeFilter] = useState('');
    const [ageRange, setAgeRange] = useState([0, 120]); // [min, max]
    const [includeUnknownAge, setIncludeUnknownAge] = useState(true);
    const [selectedManufacturers, setSelectedManufacturers] = useState([]); // Multiple selection
    const [productCodeFilter, setProductCodeFilter] = useState('');
    const [productCodeSearch, setProductCodeSearch] = useState('');
    const [isAgeFilterOpen, setIsAgeFilterOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalResults, setTotalResults] = useState(0);
    const [selectedReports, setSelectedReports] = useState(new Set());
    const [isSummarizing, setIsSummarizing] = useState(false);
    const [executedQuery, setExecutedQuery] = useState('');
    
    useEffect(() => {
        setResults(null);
    }, [query]);

    const fetchEvents = async (currentQuery, skip = 0) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await RegulifyService.searchAdverseEvents(currentQuery, skip);
            setResults(response);
            setTotalResults(response.total);
            setCurrentPage(skip / 500); // Assuming limit is 500
        } catch (err) {
            setError('Failed to fetch adverse events. Please try again.');
            console.error(err);
        }
        setIsLoading(false);
    };

    const handleSearch = (e, options = {}) => {
        const { isProductCodeSearch } = options;
        let currentQuery = query;

        if (isProductCodeSearch) {
            if (productCodeSearch.length !== 3) {
                alert("Product Code must be exactly 3 characters.");
                return;
            }
            currentQuery = `device.device_report_product_code:${productCodeSearch}`;
        } else {
            // Natural language search
            if (!query) return;
        }

        resetFilters();
        setExecutedQuery(currentQuery);
        fetchEvents(currentQuery, 0);
    };

    const handlePageChange = (newPage) => {
        const skip = newPage * 500;
        // Keep the existing query instead of resetting it
        fetchEvents(executedQuery, skip);
    };

    const resetFilters = () => {
        setEventTypeFilter('');
        setProductCodeFilter('');
        setAgeRange([0, 120]);
        setIncludeUnknownAge(true);
        setSelectedManufacturers([]);
        setSelectedReports(new Set());
    };

    const filteredEvents = useMemo(() => {
        if (!results) return [];

        return results.adverse_events.filter(event => {
            // 1. Event Type Filter
            const currentEventType = event.event?.event_type;
            const eventTypeMatch = eventTypeFilter ? currentEventType === eventTypeFilter : true;
            
            // 2. Age Filter
            const age = parseInt(event.patient?.age);
            let ageMatch = false;
            
            if (isNaN(age)) {
                ageMatch = includeUnknownAge;
            } else {
                const min = Math.min(ageRange[0], ageRange[1]);
                const max = Math.max(ageRange[0], ageRange[1]);
                ageMatch = (age >= min && age <= max);
            }

            // 3. Product Code Filter
            const productCode = event.device?.product_code;
            const productCodeMatch = productCodeFilter ? productCode === productCodeFilter : true;

            return eventTypeMatch && ageMatch && productCodeMatch;
        });
    }, [results, eventTypeFilter, ageRange, includeUnknownAge, productCodeFilter]);

    const trendData = useMemo(() => {
        if (!results) return [];
        const yearlyCounts = results.adverse_events.reduce((acc, event) => {
            const year = event.event?.date_of_event ? new Date(event.event.date_of_event).getFullYear() : null;
            if (year) {
                const manufacturer = event.device?.manufacturer_name || 'Unknown';
                const productCode = event.device?.product_code || 'Unknown';

                if ((selectedManufacturers.length === 0 || selectedManufacturers.includes(manufacturer)) &&
                    (!productCodeFilter || productCode === productCodeFilter)) {
                    if (!acc[year]) acc[year] = { year, count: 0 };
                    acc[year].count += 1;
                    if (selectedManufacturers.length > 0) {
                        acc[year][manufacturer] = (acc[year][manufacturer] || 0) + 1;
                    }
                }
            }
            return acc;
        }, {});
        return Object.values(yearlyCounts).sort((a, b) => a.year - b.year);
    }, [results, selectedManufacturers, productCodeFilter]);

    const uniqueEventTypes = useMemo(() => {
        if (!results) return [];
        const types = results.adverse_events
            .map(e => e.event?.event_type)
            .filter(Boolean);
        return [...new Set(types)].sort();
    }, [results]);

    const uniqueManufacturers = useMemo(() => {
        if (!results) return [];
        const manufacturers = results.adverse_events
            .map(e => e.device?.manufacturer_name)
            .filter(Boolean);
        return [...new Set(manufacturers)].sort();
    }, [results]);



    const uniqueProductCodes = useMemo(() => {
        if (!results) return [];
        const codes = results.adverse_events
            .map(e => e.device?.product_code)
            .filter(Boolean);
        return [...new Set(codes)].sort();
    }, [results]);

    const productCodeCounts = useMemo(() => {
        if (!results) return {};
        return results.adverse_events.reduce((acc, event) => {
            const code = event.device?.product_code;
            if (code) {
                acc[code] = (acc[code] || 0) + 1;
            }
            return acc;
        }, {});
    }, [results]);

    const sortedUniqueProductCodes = useMemo(() => {
        return Object.keys(productCodeCounts).sort((a, b) => productCodeCounts[b] - productCodeCounts[a]);
    }, [productCodeCounts]);


    const toggleReportSelection = (reportId) => {
        setSelectedReports(prev => {
            const newSet = new Set(prev);
            if (newSet.has(reportId)) {
                newSet.delete(reportId);
            } else {
                newSet.add(reportId);
            }
            return newSet;
        });
    };

    const toggleAllOnPage = () => {
        const allOnPage = filteredEvents.map(e => e.metadata.report_number);
        if (selectedReports.size === allOnPage.length) {
            // Deselect all
            setSelectedReports(new Set());
        } else {
            // Select all
            setSelectedReports(new Set(allOnPage));
        }
    };

    const handleSummarize = async () => {
        if (selectedReports.size === 0) {
            alert("Please select at least one report to summarize.");
            return;
        }

        setIsSummarizing(true);
        try {
            const selectedEventsData = filteredEvents.filter(event => 
                selectedReports.has(event.metadata.report_number)
            );
            
            const blob = await RegulifyService.summarizeAdverseEvents(selectedEventsData);
            
            // Create a link and trigger download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `Adverse_Event_Summary_Report.docx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Failed to generate summary report:", error);
            alert("An error occurred while generating the report.");
        }
        setIsSummarizing(false);
    };

    return (
        <div className="flex h-[calc(100vh-40px)] w-full flex-col">
            <ContentTitle title={"Adverse Event Search"} />

            <div className="card bg-base-100 shadow-xl border border-base-200 overflow-visible flex-1">
                <div className="card-body p-4 lg:p-6">
                    <div className="flex flex-col md:flex-row gap-2 mb-6">
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(e); }}
                            placeholder="Search by device, manufacturer, or problem (e.g., pacemaker heart failure)"
                            className="input input-bordered flex-1"
                        />
                        <button onClick={(e) => handleSearch(e)} className="btn btn-primary" disabled={isLoading || !query}>
                            {isLoading ? <span className="loading loading-spinner loading-sm"></span> : 'Search'}
                        </button>
                    </div>

                    <div className="flex justify-center items-center my-2">
                        <span className="text-sm font-bold text-gray-400">OR</span>
                    </div>
                    
                    <div className="flex flex-col md:flex-row gap-2 mb-6">
                        <input
                            type="text"
                            value={productCodeSearch}
                            onChange={(e) => setProductCodeSearch(e.target.value.toUpperCase())}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(e, { isProductCodeSearch: true }); }}
                            placeholder="Or search by 3-char Product Code (e.g., ITI)"
                            className="input input-bordered flex-1"
                            maxLength={3}
                        />
                        <button 
                            onClick={(e) => handleSearch(e, { isProductCodeSearch: true })}
                            className="btn btn-secondary"
                            disabled={isLoading || productCodeSearch.length !== 3}
                        >
                            Search by Product Code
                        </button>
                    </div>
                    
                    {/* --- FILTERS --- */}
                    {results && (
                        <div className="flex flex-col lg:flex-row gap-4 p-4 mb-6 border rounded-xl bg-base-200/50">
                            <div className="flex flex-col gap-1 min-w-[180px]">
                                <label className="text-[10px] font-bold uppercase opacity-50">Event Type</label>
                                <select 
                                    className="select select-bordered select-sm w-full"
                                    value={eventTypeFilter}
                                    onChange={(e) => setEventTypeFilter(e.target.value)}
                                >
                                    <option value="">All Event Types ({results.adverse_events.length})</option>
                                    {uniqueEventTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex flex-col gap-1 min-w-[180px]">
                                <label className="text-[10px] font-bold uppercase opacity-50">Product Code</label>
                                <select 
                                    className="select select-bordered select-sm w-full"
                                    value={productCodeFilter}
                                    onChange={(e) => setProductCodeFilter(e.target.value)}
                                >
                                    <option value="">All Product Codes</option>
                                    {sortedUniqueProductCodes.map(code => (
                                        <option key={code} value={code}>{code} ({productCodeCounts[code]})</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex flex-col gap-1 flex-1">
                                <div 
                                    className="flex justify-between items-center cursor-pointer p-2 rounded-lg hover:bg-base-300/50"
                                    onClick={() => setIsAgeFilterOpen(!isAgeFilterOpen)}
                                >
                                    <label className="text-[10px] font-bold uppercase opacity-50">
                                        Patient Age Filter
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs font-mono font-bold text-primary px-2 py-0.5 rounded-lg bg-primary/10 border border-primary/20">
                                            {ageRange[0]} - {ageRange[1]} yrs
                                        </span>
                                        <button className="btn btn-xs btn-ghost">
                                            {isAgeFilterOpen ? 'Collapse' : 'Expand'}
                                        </button>
                                    </div>
                                </div>
                                {isAgeFilterOpen && (
                                    <div className="flex flex-col gap-3 pt-4 pb-2 px-3 bg-base-100 rounded-lg border border-base-300 mt-1">
                                        <div className="flex items-center gap-3">
                                            <span className="text-[10px] uppercase opacity-40 w-8 text-right">Min</span>
                                            <input 
                                                type="range" min="0" max="120" value={ageRange[0]} 
                                                onChange={(e) => setAgeRange([parseInt(e.target.value), ageRange[1]])}
                                                className="range range-xs range-primary flex-1" 
                                            />
                                            <span className="text-[10px] font-mono w-6 text-right">{ageRange[0]}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-[10px] uppercase opacity-40 w-8 text-right">Max</span>
                                            <input 
                                                type="range" min="0" max="120" value={ageRange[1]} 
                                                onChange={(e) => setAgeRange([ageRange[0], parseInt(e.target.value)])}
                                                className="range range-xs range-primary flex-1" 
                                            />
                                            <span className="text-[10px] font-mono w-6 text-right">{ageRange[1]}</span>
                                        </div>
                                        <label className="label cursor-pointer p-0 gap-2 justify-end mt-2">
                                            <span className="label-text text-[10px] opacity-60">Include reports with unknown age</span> 
                                            <input 
                                                type="checkbox" 
                                                className="checkbox checkbox-xs checkbox-primary" 
                                                checked={includeUnknownAge}
                                                onChange={(e) => setIncludeUnknownAge(e.target.checked)}
                                            />
                                        </label>
                                    </div>
                                )}
                            </div>
                            
                            <div className="flex items-end">
                                <button className="btn btn-ghost btn-xs text-error normal-case" onClick={() => {
                                    setEventTypeFilter(''); setProductCodeFilter(''); setAgeRange([0, 120]); 
                                    setIncludeUnknownAge(true); setSelectedManufacturers([]);
                                }}>Reset All</button>
                            </div>
                        </div>
                    )}

                    {isLoading && (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <span className="loading loading-spinner loading-lg text-primary"></span>
                            <span className="text-sm opacity-60">Accessing FDA Records...</span>
                        </div>
                    )}

                    {error && <div className="alert alert-error mb-4 shadow-sm"><span>{error}</span></div>}

                    {!isLoading && results && (
                        <div className="flex flex-col gap-10">
                            {/* Results Table Section */}
                            <div className="flex flex-col">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-bold flex items-center gap-2">
                                        Clinical Reports 
                                        <div className="badge badge-secondary badge-sm">
                                            Showing {results.skip + 1} - {results.skip + filteredEvents.length} of {totalResults}
                                        </div>
                                    </h3>
                                </div>
                                
                                <div className="overflow-x-auto border rounded-xl shadow-md bg-base-100" style={{ maxHeight: '380px' }}>
                                    <table className="table table-zebra table-sm w-full table-pin-rows">
                                        <thead className="bg-base-200">
                                            <tr>
                                                <th className="p-2"><input type="checkbox" className="checkbox checkbox-sm" onChange={toggleAllOnPage} checked={selectedReports.size === filteredEvents.length} /></th>
                                                <th>Date</th>
                                                <th>Age</th>
                                                <th>Device Name</th>
                                                <th>Model</th>
                                                <th>Code</th>
                                                <th>Specialty</th>
                                                <th>Device Problems</th>
                                                <th>Patient Problems</th>
                                                <th>Manufacturer</th>
                                                <th>Type</th>
                                                <th className="text-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredEvents.map((event, index) => {
                                                const device = event.device || {};
                                                const eventDetails = event.event || {};
                                                const metadata = event.metadata || {};
                                                const mdrKey = metadata.mdr_report_key;
                                                const sourceUrl = mdrKey 
                                                    ? `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfMAUDE/detail.cfm?mdrfoi__id=${mdrKey}`
                                                    : `https://api.fda.gov/device/event.json?search=report_number:${metadata.report_number}`;

                                                return (
                                                    <tr key={index} className="hover group">
                                                        <td><input type="checkbox" className="checkbox checkbox-sm" checked={selectedReports.has(metadata.report_number)} onChange={() => toggleReportSelection(metadata.report_number)} /></td>
                                                        <td className="text-[10px] whitespace-nowrap">{eventDetails.date_of_event || "N/A"}</td>
                                                        <td className="text-[10px]">{event.patient?.age || "N/A"}</td>
                                                        <td className="max-w-[140px] truncate text-[11px] font-bold" title={device.brand_name || device.generic_name}>
                                                            {device.brand_name || device.generic_name || "N/A"}
                                                        </td>
                                                        <td className="text-[10px] font-mono">{device.model_number || "N/A"}</td>
                                                        <td className="text-[10px] font-mono">{device.product_code || "N/A"}</td>
                                                        <td className="text-[10px] max-w-[80px] truncate" title={device.medical_specialty}>{device.medical_specialty || "N/A"}</td>
                                                        <td className="max-w-[100px] truncate text-[10px]" title={eventDetails.product_problems?.join(', ')}>
                                                            {eventDetails.product_problems?.join(', ') || "N/A"}
                                                        </td>
                                                        <td className="max-w-[100px] truncate text-[10px]" title={event.patient?.problems?.join(', ')}>
                                                            {event.patient?.problems?.join(', ') || "N/A"}
                                                        </td>
                                                        <td className="max-w-[100px] truncate text-[10px]" title={device.manufacturer_name}>
                                                            {device.manufacturer_name || "N/A"}
                                                        </td>
                                                        <td>
                                                            <span className={`badge badge-xs font-bold ${
                                                                eventDetails.event_type === 'Death' ? 'badge-error text-white' : 
                                                                eventDetails.event_type === 'Injury' ? 'badge-warning' : 
                                                                'badge-ghost'
                                                            }`}>
                                                                {eventDetails.event_type || "Other"}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="btn btn-xs btn-outline btn-primary py-0 h-6 min-h-6">VIEW</a>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {filteredEvents.length === 0 && (
                                        <div className="p-20 text-center opacity-30 italic">No matching reports found.</div>
                                    )}
                                </div>
                                <div className="flex justify-between items-center mt-4">
                                    <button 
                                        className={`btn btn-primary ${isSummarizing ? 'loading' : ''}`}
                                        disabled={selectedReports.size === 0 || isSummarizing}
                                        onClick={handleSummarize}
                                    >
                                        {!isSummarizing && `Summarize (${selectedReports.size}) Selected`}
                                    </button>
                                    <div className="join">
                                        <button className="join-item btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0}>« Prev</button>
                                        <button className="join-item btn">Page {currentPage + 1}</button>
                                        <button className="join-item btn" onClick={() => handlePageChange(currentPage + 1)} disabled={results.skip + results.limit >= totalResults}>Next »</button>
                                    </div>
                                </div>
                            </div>

                            {/* Trend Chart Section */}
                            <div className="flex flex-col gap-6 border-t border-base-200 pt-10">
                                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4">
                                    <div>
                                        <h3 className="text-lg font-bold">Historical Reporting Trends</h3>
                                        <p className="text-xs opacity-50">Volume of adverse events over time based on selected criteria.</p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <div className="dropdown dropdown-top dropdown-end">
                                            <label tabIndex={0} className="btn btn-outline btn-xs m-1">
                                                Manufacturers ({selectedManufacturers.length})
                                            </label>
                                            <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-64 max-h-60 overflow-y-auto">
                                                {uniqueManufacturers.map(m => (
                                                    <li key={m}>
                                                        <label className="flex items-center gap-2 cursor-pointer">
                                                            <input 
                                                                type="checkbox" 
                                                                className="checkbox checkbox-xs"
                                                                checked={selectedManufacturers.includes(m)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) setSelectedManufacturers([...selectedManufacturers, m]);
                                                                    else setSelectedManufacturers(selectedManufacturers.filter(item => item !== m));
                                                                }}
                                                            />
                                                            {m}
                                                        </label>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <select 
                                            onChange={(e) => setProductCodeFilter(e.target.value)} value={productCodeFilter}
                                            className="select select-bordered select-xs min-w-[100px]"
                                        >
                                            <option value="">All Product Codes</option>
                                            {sortedUniqueProductCodes.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="w-full h-[350px] bg-base-100 p-6 rounded-2xl border border-base-200 shadow-sm">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={trendData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.05} />
                                            <XAxis dataKey="year" fontSize={10} tickMargin={10} axisLine={false} tickLine={false} />
                                            <YAxis fontSize={10} tickMargin={10} axisLine={false} tickLine={false} />
                                            <Tooltip 
                                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }} 
                                            />
                                            <Legend />
                                            {selectedManufacturers.length > 0 ? (
                                                selectedManufacturers.map((m, i) => (
                                                    <Line key={m} type="monotone" dataKey={m} stroke={`hsl(${(i * 360) / selectedManufacturers.length}, 70%, 50%)`} strokeWidth={3} dot={{ r: 4 }} />
                                                ))
                                            ) : (
                                                <Line type="monotone" dataKey="count" stroke="#4f46e5" strokeWidth={4} dot={{ r: 4, fill: '#4f46e5', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6, strokeWidth: 0 }} />
                                            )}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdverseEvents;
